import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authAPI } from '../api/api';
import { useAuth } from '../App';

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendState, setResendState] = useState('idle'); // idle | sending | sent

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data } = await authAPI.login(form.email, form.password);
      login(data.user, data.token);

      const routes = { restaurant: '/dashboard', driver: '/driver', admin: '/admin' };
      navigate(routes[data.user.role] || '/dashboard');
    } catch (err) {
      // 403 + needsVerification means the account exists and the password was
      // correct, but the email hasn't been verified. Distinct from bad creds.
      if (err.response?.status === 403 && err.response?.data?.needsVerification) {
        setNeedsVerification(true);
        setError('');
      } else {
        setNeedsVerification(false);
        setError(err.response?.data?.error || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    setResendState('sending');
    try {
      await authAPI.resendVerification(form.email);
    } catch (e) {
      // Endpoint always succeeds by design; swallow and show the same message.
    }
    setResendState('sent');
  }

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-black">
      {/* Left brand panel */}
      <div className="hidden md:flex md:w-1/2 bg-yellow-400 flex-col justify-between p-12 relative overflow-hidden">
        <div className="relative z-10">
          <h1
            className="text-7xl font-black text-black leading-none"
            style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}
          >
            STAR<br />FUEL
          </h1>
          <p className="text-black/70 mt-3 text-lg font-medium">
            CarFleet & Chauffeur Service
          </p>
        </div>

        <div className="relative z-10">
          <p className="text-black/60 text-sm font-medium uppercase tracking-widest mb-4">
            Built for Black-Owned Businesses
          </p>
          <div className="space-y-2">
            {['Real-time driver tracking', 'Guaranteed driver pay', 'Simple flat-rate pricing'].map((f) => (
              <div key={f} className="flex items-center gap-2 text-black/80">
                <span className="text-black font-bold">✓</span>
                <span className="text-sm font-medium">{f}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Decorative circles */}
        <div className="absolute -bottom-20 -right-20 w-64 h-64 rounded-full bg-black/10" />
        <div className="absolute top-20 -right-10 w-40 h-40 rounded-full bg-black/5" />
      </div>

      {/* Right login form */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 md:p-12">
        {/* Mobile logo */}
        <div className="md:hidden mb-8 text-center">
          <h1
            className="text-5xl font-black text-yellow-400"
            style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}
          >
            STARFUEL
          </h1>
          <p className="text-gray-500 text-sm">CarFleet & Chauffeur Service</p>
        </div>

        <div className="w-full max-w-md">
          <h2 className="text-3xl font-bold text-white mb-2">Welcome back</h2>
          <p className="text-gray-400 mb-8">Sign in to your account</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => { setForm({ ...form, email: e.target.value }); setNeedsVerification(false); setResendState('idle'); }}
                required
                className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition"
                placeholder="you@restaurant.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Password</label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition"
                placeholder="••••••••"
              />
            </div>
            <div className="text-right">
              <Link to="/forgot-password" className="text-xs text-yellow-400 hover:underline">Forgot password?</Link>
            </div>

            {needsVerification && (
              <div className="bg-yellow-400/10 border border-yellow-400/30 rounded-xl px-4 py-4 text-sm">
                <p className="text-yellow-400 font-semibold mb-1">Verify your email to continue</p>
                <p className="text-gray-400 mb-1">
                  We sent a verification link to <span className="text-gray-200">{form.email}</span>. Click it to activate your account.
                </p>
                <p className="text-gray-500 text-xs mb-3">Don't see it? Check your spam or junk folder.</p>
                {resendState === 'sent' ? (
                  <p className="text-green-400 text-xs font-medium">
                    If that account still needs verification, a new link has been sent.
                  </p>
                ) : (
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={resendState === 'sending'}
                    className="px-4 py-2 bg-yellow-400 hover:bg-yellow-300 text-black text-xs font-bold rounded-lg transition disabled:opacity-50"
                  >
                    {resendState === 'sending' ? 'Sending…' : 'Resend verification email'}
                  </button>
                )}
              </div>
            )}

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-red-400 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-xl transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <p className="mt-6 text-center text-gray-400 text-sm">
            Don't have an account?{' '}
            <Link to="/register" className="text-yellow-400 hover:text-yellow-300 font-medium">
              Create one free
            </Link>
          </p>

          {/* Demo credentials hint for dev */}
          {process.env.NODE_ENV === 'development' && (
            <div className="mt-6 p-4 bg-gray-900 rounded-xl border border-gray-700 text-xs text-gray-500">
              <p className="font-medium text-gray-400 mb-2">Dev shortcuts:</p>
              <p>Admin: admin@starfuel.com / AdminPass1</p>
              <p>Register new restaurant or driver account</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
